import { RpcError } from 'grpc-web';
import { HasPaymentMethodResponse, ShareAnalytics, VersionState } from '../../../proto/api_pb';
import { FileState } from '../../../proto/api_pb';
import { MasterMetadata } from '../../../proto/api_pb';
import { Track } from '../../../proto/api_pb';
import { TrackVersion } from '../../../proto/api_pb';
import { postTrackVersion } from '../../../services/trackService';
import { fetchTrackVersions, postTrack } from '../../../services/trackService';
import { getHasPaymentMethod } from '../../../services/stripeService';
import { handleDolbyPreviewEffect } from '../../../services/dolbyService';
import handleDolbyEffect from '../../../services/dolbyService';
import { awaitableUploadToS3 } from '../../../utils/S3Utils';
import { toast } from 'react-toastify';
import { getNextVersionNumber } from '../../../pages/MyTracks/utils';
import { AddShareAnalyticsEvent } from '../../../services/shareService';

const ASSETS_URL = process.env.REACT_APP_ASSET_S3_BUCKET_URL!;

export const downloadSong = async (
  trackUrl: string,
  title: string | undefined,
  shareId: string,
  userName: string,
  token: string
): Promise<void> => {
  try {
    const sh = new ShareAnalytics();
    sh.setShareId(shareId);
    sh.setType('DOWNLOAD');
    const response = await fetch('https://ipapi.co/json/');
    if (response.ok) {
      const data = await response.json();
      sh.setIp(data.ip);
      sh.setCountry(data.country_name);
    }
    sh.setTimestamp(Date.now());
    sh.setOmmuseUsername(userName);
    const res = await AddShareAnalyticsEvent(token, sh);
    if (res instanceof RpcError) toast.warn('Failed to update analytics event');
    const link = document.createElement('a');
    link.href = trackUrl;
    link.download = title!;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {}
};

export const handleFetchTrackVersions = async (
  token: string,
  id: string
): Promise<
  | {
      track: TrackVersion;
      isPlaying: boolean;
    }[]
  | undefined
> => {
  try {
    const response = await fetchTrackVersions(token, id!);
    if (!response) return undefined;
    if (response instanceof RpcError) return undefined;
    const newTrackVersionList = response.getTrackVersionsList().map((el) => {
      return { track: el, isPlaying: false };
    });
    return newTrackVersionList;
  } catch (e) {
    const error = e as RpcError;
  }
};

export const handleGetNextVersionNumber = async (token: string, id: string): Promise<number> => {
  try {
    const response = await fetchTrackVersions(token, id!);
    if (!response) return 0;
    if (response instanceof RpcError) return 0;
    const newTrackVersionList = response.getTrackVersionsList();
    newTrackVersionList.sort((a, b) => b.getVersionNumber() - a.getVersionNumber());
    const nextVersionNumList = getNextVersionNumber(
      newTrackVersionList[0].getVersionNumber(),
      true
    );

    return parseFloat(nextVersionNumList[0]);
  } catch (e) {
    const error = e as RpcError;
    return 0;
  }
};

export const getMasterAudioDuration = (url: string): Promise<number> => {
  return new Promise((resolve, reject) => {
    const audio = new Audio();
    audio.src = url; // Use the provided URL directly
    audio.onloadedmetadata = () => {
      resolve(audio.duration);
    };
    audio.onerror = (error) => {
      resolve(0); // Return 0 if there is an error loading the audio
    };
  });
};

export const getPaymentMethod = async (
  token: string,
  userId: string
): Promise<HasPaymentMethodResponse | undefined> => {
  try {
    const response = (await getHasPaymentMethod(token, userId!)) as HasPaymentMethodResponse;
    return response;
  } catch (e) {
    const error = e as RpcError;
  }
};

const handleError = () => {
  toast.error('Error');
};

export const addDolbyEffect = (
  selectedPresetArray: string[],
  sliderValue: number,
  token: string,
  trackUrl: string,
  uuid: string,
  setPreviewUrl: (url: Map<string, string>) => void,
  setPreviewLoading: (loading: boolean) => void,
  handlePreviewState: (state: string) => void,
  previewStartValue: number | undefined,
  duration: number | undefined
): void => {
  handleDolbyPreviewEffect(
    token,
    handleError,
    setPreviewUrl,
    setPreviewLoading,
    handlePreviewState,
    selectedPresetArray,
    trackUrl,
    uuid,
    sliderValue,
    previewStartValue,
    duration
  );
};

export const handleAddTrackVersion = (
  selectedPresetValue: string,
  sliderValue: number,
  token: string,
  trackUrl: string,
  uuid: string,
  setMasteredMusicUrl: (url: string) => void,
  setPreviewLoading: (loading: boolean) => void,
  handleState: (state: string) => void,
  setJobId?: (jobId: string) => void,
  isFree?: boolean
): void => {
  handleDolbyEffect(
    token,
    handleError,
    setMasteredMusicUrl,
    setPreviewLoading,
    handleState,
    selectedPresetValue,
    trackUrl,
    uuid,
    sliderValue,
    setJobId,
    isFree
  );
};

export const addNewVersion = async (
  track: Track | undefined,
  masteredMusicUrl: string | undefined,
  selectedPreset: string,
  jobId: string | undefined,
  previewUrl: string | undefined,
  token: string,
  id: string,
  userId: string,
  handleRefreshTracks?: () => Promise<void>,
  sliderValue?: number,
  enterpriseId?: string
): Promise<void> => {
  try {
    if (track && masteredMusicUrl) {
      const trackVersion = new TrackVersion();
      // TODO: expose label entry field here for adding comment/notes/name
      trackVersion.setLabel(`Mastered by dolby with preset: ${selectedPreset}`);
      trackVersion.setTrackUrl(masteredMusicUrl);
      trackVersion.setTrackId(track.getId());

      const songLength = await getMasterAudioDuration(masteredMusicUrl);
      trackVersion.setLengthSeconds(songLength);

      // gets the next version number
      const nextVersionNum = await handleGetNextVersionNumber(token, track.getId());
      trackVersion.setVersionNumber(nextVersionNum);
      trackVersion.setVersionState(VersionState.VERSION_STATE_READY_FOR_PUBLISHING);
      trackVersion.setFileState(FileState.FILE_STATE_ACTIVE);
      trackVersion.setTrackUrl(masteredMusicUrl);
      trackVersion.setIsPrimary(true);
      if (jobId && masteredMusicUrl && previewUrl && selectedPreset) {
        const masterMetadata = new MasterMetadata();
        masterMetadata.setId(track.getId());
        masterMetadata.setState(2);
        masterMetadata.setJobId(jobId);
        masterMetadata.setLoudnessProfile(sliderValue ? sliderValue.toString() : '0');
        masterMetadata.setDolbyMasterUrl(masteredMusicUrl);
        masterMetadata.setDolbyPreviewUrl(previewUrl);
        masterMetadata.setMasteringProfile(selectedPreset);
        masterMetadata.setTimestamp(Date.now());
        trackVersion.setMasterMetadata(masterMetadata);
      }
      const postTrackVersionResponse = await postTrackVersion(
        trackVersion,
        token,
        userId,
        enterpriseId
      );
      if (postTrackVersionResponse instanceof RpcError) {
        //TODO: handle api exception gracefully with error modal, etc
        throw postTrackVersionResponse;
      }
      track.setTrackVersionId(postTrackVersionResponse.getTrackVersion()!.getId());
      const postTrackResponse = await postTrack(track, token, userId);
      if (postTrackResponse instanceof RpcError) {
        //TODO: handle api exception gracefully with error modal, etc
        throw postTrackResponse;
      }
    }
    if (handleRefreshTracks) await handleRefreshTracks();
  } catch (error) {
    throw error;
  }
};

export const onDownload = async (
  file: File,
  awsAccessKeyId: string | undefined,
  awsSecretAccessKey: string | undefined,
  awsSessionToken: string | undefined,
  userId: string
): Promise<string> => {
  const masteredMusicKey = await awaitableUploadToS3(
    userId,
    file,
    `song`,
    file.type,
    awsAccessKeyId,
    awsSecretAccessKey,
    awsSessionToken,
    () => {}
  );
  const finalMasteredMusicUrl = ASSETS_URL + '/' + masteredMusicKey;
  return finalMasteredMusicUrl;
};

export const onMasterDownload = async (
  file: File,
  awsAccessKeyId: string | undefined,
  awsSecretAccessKey: string | undefined,
  awsSessionToken: string | undefined,
  userId: string
): Promise<string> => {
  const masteredMusicKey = await awaitableUploadToS3(
    userId,
    file,
    `song`,
    file.type,
    awsAccessKeyId,
    awsSecretAccessKey,
    awsSessionToken,
    () => {}
  );
  const finalMasteredMusicUrl = ASSETS_URL + '/' + masteredMusicKey;
  return finalMasteredMusicUrl;
};
